import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Heart, Users, Shield, Globe } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const AboutUs = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-red-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
              About FlameLink
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Revolutionizing online dating with AI-powered matching and meaningful connections
          </p>
        </div>

        <div className="space-y-8">
          {/* Our Story */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Heart className="w-6 h-6 text-red-500 mr-3" />
              Our Story
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              FlameLink was born from a simple belief: everyone deserves to find meaningful love. Founded in 2023, 
              we set out to solve the biggest challenge in online dating - creating genuine connections in a digital world.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Unlike traditional dating apps that focus on superficial swiping, FlameLink uses advanced AI algorithms 
              to analyze compatibility based on personality, values, lifestyle preferences, and relationship goals. We believe 
              that lasting relationships are built on deeper connections, not just physical attraction.
            </p>
          </Card>

          {/* Our Mission */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Globe className="w-6 h-6 text-blue-500 mr-3" />
              Our Mission
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To create a world where finding love is accessible, authentic, and empowering for everyone. We're committed to:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Building technology that prioritizes meaningful connections over quantity
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Creating a safe, inclusive environment for all users regardless of background
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Continuously innovating to improve the dating experience
              </li>
            </ul>
          </Card>

          {/* Our Values */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Shield className="w-6 h-6 text-green-500 mr-3" />
              Our Values
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Authenticity</h3>
                  <p className="text-gray-600 text-sm">We promote genuine profiles and real connections through photo verification and identity checks.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Safety First</h3>
                  <p className="text-gray-600 text-sm">Your safety is our priority with advanced moderation, reporting tools, and safety guidelines.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Innovation</h3>
                  <p className="text-gray-600 text-sm">We continuously improve our matching algorithms using the latest AI and machine learning technologies.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Inclusivity</h3>
                  <p className="text-gray-600 text-sm">Love knows no boundaries. We welcome users of all backgrounds, orientations, and relationship preferences.</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Team */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Users className="w-6 h-6 text-purple-500 mr-3" />
              Our Community
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              FlameLink is built by a diverse team of engineers, designers, and relationship experts who are passionate 
              about creating technology that brings people together. Our community includes amazing people from all walks of life:
            </p>
            
            {/* Community Showcase */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_swipeflame/artifacts/7p00i0jr_6ba90f27-102a-4465-8cae-041213dd2827_removalai_preview.png"
                  alt="Community Member"
                  className="w-20 h-20 rounded-full mx-auto mb-2 object-cover shadow-lg"
                />
                <p className="text-sm text-gray-600">Creative Professional</p>
              </div>
              <div className="text-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_swipeflame/artifacts/9jy4wn7q_6cf56312-681f-4a9c-b3a0-45e5c0f7f561_removalai_preview.png"
                  alt="Community Member"
                  className="w-20 h-20 rounded-full mx-auto mb-2 object-cover shadow-lg"
                />
                <p className="text-sm text-gray-600">Healthcare Hero</p>
              </div>
              <div className="text-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_swipeflame/artifacts/3hggnxml_6e3fd75c-04d5-4b68-885e-9c90e80f162f_removalai_preview.png"
                  alt="Community Member"
                  className="w-20 h-20 rounded-full mx-auto mb-2 object-cover shadow-lg"
                />
                <p className="text-sm text-gray-600">Marketing Strategist</p>
              </div>
              <div className="text-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_swipeflame/artifacts/hknd70p8_7b6c0b76-5cf2-40d0-a001-e23330e7d656_removalai_preview.png"
                  alt="Community Member"
                  className="w-20 h-20 rounded-full mx-auto mb-2 object-cover shadow-lg"
                />
                <p className="text-sm text-gray-600">Tech Entrepreneur</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-lg p-6">
              <p className="text-gray-700 italic text-center">
                "We're not just building an app - we're building the future of how people find love."
              </p>
              <p className="text-gray-600 text-sm text-center mt-2">- The FlameLink Community</p>
            </div>
          </Card>

          {/* Contact CTA */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Want to Learn More?</h3>
            <Button
              onClick={() => window.open('mailto:hello@flamelink.com', '_blank')}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-8 py-3 rounded-full"
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;